/**
 * Cash the sales dashboard can stand behind: Stripe amounts in the
 * currency that was charged. There is no blended pound/dollar total.
 */

export interface RecordedRevenue {
  revenue_gbp?: number | null;
  revenue_usd?: number | null;
  revenue_eur?: number | null;
}

export interface RecordedRevenueCaption {
  credits_granted?: number | null;
  credits_sold?: number | null;
  sales_without_amount?: number | null;
}

export function formatRecordedRevenue(metrics: RecordedRevenue): string {
  const parts: string[] = [];
  const gbp = Number(metrics.revenue_gbp) || 0;
  const usd = Number(metrics.revenue_usd) || 0;
  const eur = Number(metrics.revenue_eur) || 0;
  if (gbp > 0) parts.push(`£${gbp.toFixed(2)}`);
  if (usd > 0) parts.push(`$${usd.toFixed(2)}`);
  if (eur > 0) parts.push(`€${eur.toFixed(2)}`);
  return parts.length > 0 ? parts.join(' · ') : '£0.00';
}

export function formatChargedAmount(
  cents: number | null | undefined,
  currency: string | null | undefined,
): string {
  if (cents == null || !Number.isFinite(Number(cents)) || !currency) return '—';
  const major = Number(cents) / 100;
  const code = currency.toUpperCase();
  if (code === 'GBP') return `£${major.toFixed(2)}`;
  if (code === 'USD') return `$${major.toFixed(2)}`;
  if (code === 'EUR') return `€${major.toFixed(2)}`;
  return `${major.toFixed(2)} ${code}`;
}

export function recordedRevenueCaption(metrics: RecordedRevenueCaption): string {
  const credits = Number(metrics.credits_granted ?? metrics.credits_sold ?? 0);
  const missing = Number(metrics.sales_without_amount ?? 0);
  const base = `${credits} credits granted`;
  if (missing > 0) {
    const noun = missing === 1 ? 'sale has' : 'sales have';
    return `${base} · ${missing} ${noun} no Stripe amount`;
  }
  return base;
}

/**
 * Provider API cost for the sales panels.
 * A zero total means the pipeline stored the column default, not a measured cost.
 * Unknown is not £0 and must not be used to claim a margin.
 */
export function formatProviderCostUsd(
  amount: number | null | undefined,
  options?: { generations?: number },
): string {
  const generations = options?.generations ?? 0;
  const value = Number(amount);
  if (!Number.isFinite(value) || value <= 0) {
    return generations > 0 ? 'Unknown' : '—';
  }
  return `$${value.toFixed(4)}`;
}

/** USD cash minus USD API cost, only when every recorded charge is in USD. */
export function usdOnlyMargin(metrics: RecordedRevenue & { total_cost_usd?: number | null }): number | null {
  const gbp = Number(metrics.revenue_gbp) || 0;
  const usd = Number(metrics.revenue_usd) || 0;
  const eur = Number(metrics.revenue_eur) || 0;
  if (usd <= 0 || gbp > 0 || eur > 0) return null;
  return usd - (Number(metrics.total_cost_usd) || 0);
}
